import axios from 'axios';
import { BASE_URL } from 'constants/api';
import {
  ContentThumbnailOverride,
  CreateThumbnailOverrideRequest,
  UpdateThumbnailOverrideRequest,
} from '../types/thumbnailOverrideType';

class ThumbnailOverrideService {
  private getBasePath(contentId: number): string {
    return `${BASE_URL}/api/v1/content/${contentId}/thumbnail-override/`;
  }

  /**
   * Override 썸네일 목록 조회
   */
  async fetchList(contentId: number): Promise<ContentThumbnailOverride[]> {
    const response = await axios.get(this.getBasePath(contentId));
    // API 응답이 배열이면 직접 반환, 객체면 results 필드 사용
    if (Array.isArray(response.data)) {
      return response.data;
    }
    // 페이지네이션 형태의 응답 처리
    if (response.data && Array.isArray(response.data.results)) {
      return response.data.results;
    }
    return [];
  }

  /**
   * Override 썸네일 상세 조회
   */
  async fetchById(contentId: number, thumbnailId: number): Promise<ContentThumbnailOverride> {
    const response = await axios.get(`${this.getBasePath(contentId)}${thumbnailId}/`);
    return response.data;
  }

  /**
   * Override 썸네일 생성 (파일 업로드)
   */
  async createWithFile(
    contentId: number,
    file: File,
    language: string,
    orientation: string = 'landscape'
  ): Promise<ContentThumbnailOverride> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', language);
    formData.append('orientation', orientation);

    const response = await axios.post(this.getBasePath(contentId), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Override 썸네일 생성 (외부 URL)
   */
  async createWithUrl(
    contentId: number,
    request: CreateThumbnailOverrideRequest
  ): Promise<ContentThumbnailOverride> {
    const response = await axios.post(this.getBasePath(contentId), {
      external_url: request.external_url,
      language: request.language,
      orientation: request.orientation || 'landscape',
      width: request.width,
      height: request.height,
    });
    return response.data;
  }

  /**
   * Override 썸네일 수정 (파일)
   */
  async updateWithFile(
    contentId: number,
    thumbnailId: number,
    file: File,
    orientation?: string
  ): Promise<ContentThumbnailOverride> {
    const formData = new FormData();
    formData.append('file', file);
    if (orientation) {
      formData.append('orientation', orientation);
    }

    const response = await axios.patch(`${this.getBasePath(contentId)}${thumbnailId}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Override 썸네일 수정 (URL)
   */
  async updateWithUrl(
    contentId: number,
    thumbnailId: number,
    request: UpdateThumbnailOverrideRequest
  ): Promise<ContentThumbnailOverride> {
    const response = await axios.patch(`${this.getBasePath(contentId)}${thumbnailId}/`, {
      external_url: request.external_url,
      orientation: request.orientation,
      width: request.width,
      height: request.height,
    });
    return response.data;
  }

  /**
   * Override 썸네일 삭제
   */
  async delete(contentId: number, thumbnailId: number): Promise<{ message: string }> {
    const response = await axios.delete(`${this.getBasePath(contentId)}${thumbnailId}/`);
    return response.data;
  }
}

export const thumbnailOverrideService = new ThumbnailOverrideService();
